import React from 'react';
import {
  Beaker,
  CheckList,
  Clipboard,
  FileText,
  Helix,
  Home,
  PlayCircle,
  Procedure,
  SparkleCircle,
  SparkleFile,
  Tablet,
} from '@lifeomic/chromicons';
import { memberDetailMessages } from './messages';
import { MemberSection } from './types';
import urls from '../../../../util/urls';

type MemberDetailSectionRequirement =
  | 'fountainLifePowerUser'
  | 'memberAppViewing'
  | 'genomics';

export type MemberDetailSectionConfig = {
  section: MemberSection;
  labelMessage: keyof typeof memberDetailMessages;
  menuMessage: keyof typeof memberDetailMessages;
  descriptionMessage?: keyof typeof memberDetailMessages;
  icon: React.ComponentType<{ className?: string }>;
  requirement?: MemberDetailSectionRequirement;
  showOnLanding: boolean;
  quickAccess?: boolean;
};

export type MemberDetailSectionVisibility = {
  fountainLifePowerUserEnabled: boolean;
  memberAppViewingEnabled: boolean;
  memberDetailsLandingEnabled?: boolean;
  includeGenomics?: boolean;
};

export const MEMBER_DETAIL_SECTION_CONFIGS: MemberDetailSectionConfig[] = [
  {
    section: 'landing',
    labelMessage: 'landingLabel',
    menuMessage: 'landingTitle',
    icon: Home,
    showOnLanding: false,
  },
  {
    section: 'onboarding-snapshot',
    labelMessage: 'onboardingSnapshotLabel',
    menuMessage: 'onboardingSnapshot',
    descriptionMessage: 'landingOnboardingSnapshotDescription',
    icon: PlayCircle,
    showOnLanding: true,
  },
  {
    section: 'annual-report',
    labelMessage: 'annualReportLabel',
    menuMessage: 'annualReport',
    descriptionMessage: 'landingAnnualReportDescription',
    icon: FileText,
    showOnLanding: true,
    quickAccess: true,
  },
  {
    section: 'action-plan',
    labelMessage: 'actionPlanLabel',
    menuMessage: 'actionPlan',
    descriptionMessage: 'landingActionPlanDescription',
    icon: CheckList,
    showOnLanding: true,
  },
  {
    section: 'ai-tools',
    labelMessage: 'aiToolsLabel',
    menuMessage: 'aiTools',
    descriptionMessage: 'landingAiToolsDescription',
    icon: SparkleFile,
    showOnLanding: true,
    quickAccess: true,
  },
  {
    section: 'rule-action-audit',
    labelMessage: 'ruleActionAuditLabel',
    menuMessage: 'ruleActionAudit',
    descriptionMessage: 'landingRuleActionAuditDescription',
    icon: Procedure,
    requirement: 'fountainLifePowerUser',
    showOnLanding: true,
  },
  {
    section: 'labs',
    labelMessage: 'labsLabel',
    menuMessage: 'labsAndData',
    descriptionMessage: 'landingLabsDescription',
    icon: Beaker,
    showOnLanding: true,
    quickAccess: true,
  },
  {
    section: 'documents',
    labelMessage: 'documentsLabel',
    menuMessage: 'documents',
    descriptionMessage: 'landingDocumentsDescription',
    icon: Clipboard,
    showOnLanding: true,
  },
  {
    section: 'surveys',
    labelMessage: 'surveysLabel',
    menuMessage: 'surveys',
    descriptionMessage: 'landingSurveysDescription',
    icon: CheckList,
    showOnLanding: true,
  },
  {
    section: 'member-app',
    labelMessage: 'memberAppLabel',
    menuMessage: 'memberAppLabel',
    descriptionMessage: 'landingMemberAppDescription',
    icon: Tablet,
    requirement: 'memberAppViewing',
    showOnLanding: true,
  },
  {
    section: 'omics-explorer',
    labelMessage: 'omicsExplorerLabel',
    menuMessage: 'omicsExplorer',
    descriptionMessage: 'landingOmicsExplorerDescription',
    icon: Helix,
    requirement: 'genomics',
    showOnLanding: true,
  },
];

export const ZORI_CHAT_TILE_CONFIG = {
  labelMessage: 'zoriChatLabel',
  descriptionMessage: 'landingZoriChatDescription',
  icon: SparkleCircle,
  quickAccess: true,
} as const;

export const getVisibleMemberDetailSections = ({
  fountainLifePowerUserEnabled,
  memberAppViewingEnabled,
  memberDetailsLandingEnabled = false,
  includeGenomics = true,
}: MemberDetailSectionVisibility) =>
  MEMBER_DETAIL_SECTION_CONFIGS.filter(config => {
    if (config.section === 'landing') {
      return memberDetailsLandingEnabled;
    }
    if (config.requirement === 'fountainLifePowerUser') {
      return fountainLifePowerUserEnabled;
    }
    if (config.requirement === 'memberAppViewing') {
      return memberAppViewingEnabled;
    }
    if (config.requirement === 'genomics') {
      return includeGenomics;
    }
    return true;
  });

export const getMemberDetailSectionConfig = (section: MemberSection) =>
  MEMBER_DETAIL_SECTION_CONFIGS.find(config => config.section === section);

export const getMemberDetailSectionPath = (
  memberId: string,
  section: MemberSection,
) =>
  section === 'landing'
    ? urls.app.members(memberId)
    : `${urls.app.members(memberId)}/${section}`;
